import { styled, useTheme } from "@mui/system";
import { capitalize } from "lodash";
import InfoIcon from "src/icons/portfolio/InfoIcon";
import Preloader from "src/utils/Preloader";
import TimePeriodDropdown from "../balance/TimePeriodDropdown";
import { ISummaryData, ITimePeriodOption, SUMMARY_TOOLTIP, SUMMARY_TOOLTIPS } from "../defaults";
import QuestionMarkIcon from "src/icons/portfolio/QuestionMarkIcon";

export const SummaryHeader = styled("div")({
  display: "flex",
  alignSelf: "stretch",
  justifyContent: "space-between",
  alignItems: "center",
});

export const SummarySubHeader = styled("div")({
  display: "flex",
  width: "138px",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
  fontSize: "14px",
});

export const SummaryTileWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  height: "136px",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",

  [theme.breakpoints.between(720, 1440)]: {
    height: "62px",
    alignItems: "flex-start",
    alignSelf: "stretch",
    flexDirection: "row",
  },
}));

export const SummaryTileRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  width: "100%",
});

export const SummaryTile = styled("div")(({ theme }) => ({
  width: "100%",
  height: "62px",
  padding: "8px",
  borderRadius: "10px",
  border: `1px solid ${theme.palette.secondary.contrastText}`,
  background: theme.palette.secondary.contrastText,
}));

export const SummaryTileMetric = styled("div")({
  fontWeight: 600,
  fontSize: "16px",
});

export const SummaryTileText = styled("div")({
  display: "flex",
  fontSize: "14px",
  alignItems: "center",
  gap: "8px",
});

interface PortfolioSummaryProps {
  data: ISummaryData[];
  timePeriodOptions: ITimePeriodOption[];
  timePeriod: string;
  handleTimePeriod: (newOpt: ITimePeriodOption) => void;
  isFetching: boolean;
}

const PortfolioSummary = (props: PortfolioSummaryProps) => {
  const { data, timePeriodOptions, timePeriod, handleTimePeriod, isFetching } = props;

  const firstArray = data.slice(0, 2);
  const secondArray = data.slice(2);

  const theme = useTheme();
  return (
    <>
      {/* HEADER */}
      <SummaryHeader>
        <SummarySubHeader>
          Your summary{" "}
          <InfoIcon
            tooltip={SUMMARY_TOOLTIP}
            color={theme.palette.mode === "light" ? undefined : "#CFD6E4"}
          />
        </SummarySubHeader>
        <TimePeriodDropdown
          options={timePeriodOptions}
          timePeriod={timePeriod}
          onTimePeriodChange={handleTimePeriod}
        />
      </SummaryHeader>

      {isFetching ? (
        <Preloader />
      ) : (
        <>
          {/* TILES */}
          <SummaryTileWrapper>
            <SummaryTileRow>
              {firstArray.map((el) => (
                <SummaryTile key={el.type}>
                  <SummaryTileMetric id={`user-summary-${el.type}`} translate="no">
                    {el.value}
                  </SummaryTileMetric>
                  <SummaryTileText>
                    {capitalize(el.type)}
                    <QuestionMarkIcon
                      tooltip={SUMMARY_TOOLTIPS[capitalize(el.type)]}
                      color={theme.palette.mode === "light" ? undefined : "#CFD6E4"}
                    />
                  </SummaryTileText>
                </SummaryTile>
              ))}
            </SummaryTileRow>

            <SummaryTileRow>
              {secondArray.map((el) => (
                <SummaryTile key={el.type}>
                  <SummaryTileMetric id={`user-summary-${el.type}`} translate="no">
                    {el.value}
                  </SummaryTileMetric>
                  <SummaryTileText>
                    {capitalize(el.type)}
                    <QuestionMarkIcon
                      tooltip={SUMMARY_TOOLTIPS[capitalize(el.type)]}
                      color={theme.palette.mode === "light" ? undefined : "#CFD6E4"}
                    />
                  </SummaryTileText>
                </SummaryTile>
              ))}
            </SummaryTileRow>
          </SummaryTileWrapper>
        </>
      )}
    </>
  );
};

export default PortfolioSummary;
